import { useParams } from 'react-router-dom';
import { useState } from 'react';
import {
  useGetAuthInspectQuery,
  useGetProfilesByUserIdQuery,
  useGetTripsByUserByUserIdQuery,
} from '../../store/backendApi';
import { BoxProps, TripBoxes } from '../../components/tripBoxes';
import { CameraIcon, EnvelopeIcon, TrashIcon, UserIcon } from '@heroicons/react/24/outline';
import { LoadingTripBoxes } from '../../components/loadingTripBoxes';
import { LoadingChildren } from '../../components/loadingChildren';
import { LoadingChildrenBox } from '../../components/loadingChildrenBox';
import { PageProps } from '../../shell/shell';
import EmailChangeModal from './emailChangeModal';
import EmailVerificationModal from './emailVerificationModal';
import DeleteAccountModal from './deleteAccountModal';

const thumbnail = (path: string) => path.replace(/(\.[^.]+)$/, `-thumbnail$1`);
const tripUrl = (
  tripId: string,
  ownerName: string,
  shareMode: 'PUBLIC' | 'LINK' | 'PRIVATE',
  slug: string | undefined,
  linkSecret: string | undefined,
) =>
  shareMode === 'PRIVATE'
    ? `/p/trip/${tripId}`
    : shareMode === 'PUBLIC'
      ? `/trip/${ownerName}/${slug}`
      : `/trip/${linkSecret}`;
const tripHeaderUrl = (tripId: string, fileName: string) =>
  thumbnail(`https://media.${process.env.REACT_APP_HOST}/${tripId}/${fileName}`);

export const ProfilePage = (props: PageProps) => {
  const params = useParams();
  const userId = params['userId'];
  const [showEmailChangeModal, setShowEmailChangeModal] = useState(false);
  const [showEmailVerificationModal, setShowEmailVerificationModal] = useState(false);
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [pendingEmailChange, setPendingEmailChange] = useState<{
    accessToken: string;
    newEmail: string;
  } | null>(null);

  if (!userId) {
    return (
      <div>
        <p>No user id</p>
      </div>
    );
  }

  const { data: authData } = useGetAuthInspectQuery();
  const isOwnProfile = props.user?.userId === userId;

  const {
    data: tripsData,
    isLoading: tripsLoading,
    isError: tripsLoadError,
  } = useGetTripsByUserByUserIdQuery(userId);

  const {
    data: profileData,
    isLoading: profileLoading,
    isError: profileLoadError,
  } = useGetProfilesByUserIdQuery(userId);

  let trips: BoxProps[] | undefined = undefined;
  if (tripsData && profileData) {
    trips = tripsData.map((trip) => ({
      title: trip.title,
      href: tripUrl(trip.id, profileData.username, trip.shareMode, trip.slug, trip.linkSecret),
      imageUrl: tripHeaderUrl(trip.id, trip.headerFileName),
      startDate: trip.startDate,
      endDate: trip.endDate,
      count: trip.postCount.toString(),
      role: '',
      ownerId: userId,
    }));
  }

  const handleEmailChangeInitiated = (accessToken: string, newEmail: string) => {
    setPendingEmailChange({ accessToken, newEmail });
    setShowEmailVerificationModal(true);
  };

  const handleEmailVerified = () => {
    setPendingEmailChange(null);
    // Optionally refresh the page or show a success message
    window.location.reload();
  };

  const handleAccountDeleted = () => {
    window.location.href = '/signout';
  };

  if (tripsLoadError) {
    return <p>could not find profile</p>;
  }
  if (profileLoadError) {
    return <p>could not load profile</p>;
  }

  return (
    <div className="w-full mt-6">
      <LoadingChildrenBox
        loading={profileLoading}
        loadingElement={
          <div className="rounded-full object-cover h-28 w-28 m-auto animate-pulse bg-gray-300" />
        }
      >
        <img
          src={`https://media.${
            process.env.REACT_APP_HOST
          }/users/${userId}-thumbnail.jpg?v=${Date.now()}`}
          id="profile-image"
          alt={profileData?.username}
          onError={(e) => (e.currentTarget.src = '/default-profile-picture.jpg')}
          className="rounded-full object-cover h-28 w-28 m-auto"
        />
      </LoadingChildrenBox>
      <div className="mt-1 flex items-center justify-center">
        <LoadingChildren
          expectedChars={10}
          loadingClassName="mt-1 text-2xl/7 m-auto font-extrabold"
          loading={profileLoading}
        >
          <h2 className="mt-1 text-2xl/7 text-center font-extrabold">{profileData?.username}</h2>
        </LoadingChildren>
      </div>
      <div className="mt-1 flex items-center justify-center">
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-4">
          <LoadingChildren
            expectedChars={12}
            loadingClassName="inline-flex items-center px-2 py-1 text-xs font-extrabold"
            loading={profileLoading}
          >
            <p
              style={{ color: '#023759' }}
              className="inline-flex items-center px-2 py-1 text-xs font-extrabold"
            >
              <UserIcon className="w-4 h-4 mr-1" />
              Since: {profileData?.creationYear ?? ''}
            </p>
          </LoadingChildren>
          <LoadingChildren
            expectedChars={12}
            loadingClassName="inline-flex items-center px-2 py-1 text-xs font-extrabold"
            loading={tripsLoading}
          >
            <p
              style={{ color: '#023759' }}
              className="inline-flex items-center px-2 py-1 text-xs font-extrabold"
            >
              <CameraIcon className="w-4 h-4 mr-1" />
              Trips: {trips?.length ?? ''}
            </p>
          </LoadingChildren>
        </div>
      </div>
      {isOwnProfile && (
        <div className="mt-4 flex items-center justify-center gap-4">
          <button
            onClick={() => setShowEmailChangeModal(true)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-indigo-600
            hover:text-indigo-500 focus:outline-none focus:underline"
          >
            <EnvelopeIcon className="w-4 h-4 mr-2" />
            Change Email ({props.user?.email})
          </button>
          <button
            onClick={() => setShowDeleteAccountModal(true)}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600
            hover:text-red-500 focus:outline-none focus:underline"
          >
            <TrashIcon className="w-4 h-4 mr-2" />
            Delete Account
          </button>
        </div>
      )}
      <div className="mt-1 flex items-center justify-center">
        <LoadingChildren
          expectedChars={12}
          loadingClassName="mt-1 text-md text-center m-auto"
          loading={tripsLoading}
        >
          <p className="mt-1 text-md text-center sm:w-[50%] m-auto">No profile description.</p>
        </LoadingChildren>
      </div>
      <div className="mt-16 w-full">
        {trips && <TripBoxes trips={trips} />}
        {!trips && <LoadingTripBoxes />}
      </div>

      {props.user?.email && (
        <EmailChangeModal
          open={showEmailChangeModal}
          setOpen={setShowEmailChangeModal}
          onEmailChangeInitiated={handleEmailChangeInitiated}
          currentEmail={props.user.email}
        />
      )}

      {/* Email Verification Modal */}
      {pendingEmailChange && (
        <EmailVerificationModal
          open={showEmailVerificationModal}
          setOpen={setShowEmailVerificationModal}
          newEmail={pendingEmailChange.newEmail}
          accessToken={pendingEmailChange.accessToken}
          onEmailVerified={handleEmailVerified}
        />
      )}

      {/* Delete Account Modal */}
      {props.user?.email && (
        <DeleteAccountModal
          open={showDeleteAccountModal}
          setOpen={setShowDeleteAccountModal}
          currentEmail={props.user.email}
          onAccountDeleted={handleAccountDeleted}
        />
      )}
    </div>
  );
};
